import React, { type JSX } from 'react';
import { JoinFull, JoinInner, Delete, ChevronRight, AddBox, Search, DynamicForm, Link } from '@mui/icons-material';
import {
    CustomIcon,
    CustomTable,
    CustomTransComponent,
    FailedLoadTriggerModal,
    GroupPath,
    StepperModal,
} from 'components';
import {
    CircularProgress,
    Skeleton,
    IconButton,
    Button,
    Chip,
    Typography,
    Box,
    Stack,
    FormHelperText,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputLabel,
    MenuItem,
    Paper,
} from '@mui/material';
import * as Colors from '@brightlayer-ui/colors';
import { CustomSwitchBtn, type ChangeTrigger, type GroupState } from 'components/CustomSwitchBtn/CustomSwitchBtn';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import {
    useCreateLoadGroupsMutation,
    useDeleteLoadGroupsMutation,
    useGetLoadGroupsListQuery,
    useGetMultiDeviceQuery,
    useGetDeviceByIdForLoadGroupsQuery,
    useUpdateLoadGroupsMutation,
    // useGetloadGroupsByIdQuery,
} from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useConfirm, useDebounce } from '@fiji/common/src/hooks';
import { useDispatch } from 'react-redux';
import { InputWithCustomPlaceholder } from 'components/InputWithCustomPlaceholder';
import { toFirstLetterUpperCase } from 'utils/helpers';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '@fiji/common';
import { LoadStepTwo } from './LoadStepTwo';
import { LoadHierarchy } from './LoadHierarchy';
import { MultiDeviceLoadStep } from './MultiDeviceLoadStep';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
// BSSRM/COMMAND_TOPIC/0a06a882-4818-4997-bf6d-589d6c43e80d/9024535b-e7f5-452c-a632-05d7340dae26/4616e1ff-2a35-4742-ba5d-f2b714d77596/664cfa54-7c2a-48ae-9206-71d79942ba81
// import { useFilteredValues } from 'hooks/useFilteredValues';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { useNewCustomTranslations, useRBAC, useTextFieldError } from 'hooks';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { setFailedLoadTrigger } from '@fiji/common/src/features/loadGroups/loadGroupsSlice';
import { buildLoadGroupMqttTopicsForRecords } from '@fiji/common/src/utils/loadGroupMqtt';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

const VALID_MQTT_GROUP_STATES: readonly GroupState[] = ['ENABLED', 'DISABLED', 'PARTIAL', 'PENDING'];

const extractMqttStringState = (message: any): GroupState | null => {
    const candidate = message?.groupState ?? message?.loadState ?? message?.state;
    if (typeof candidate !== 'string') return null;
    const upper = candidate.toUpperCase() as GroupState;
    return VALID_MQTT_GROUP_STATES.includes(upper) ? upper : null;
};

const extractMqttBoolState = (message: any): GroupState | null => {
    if (typeof message?.isEnabled === 'boolean') return message.isEnabled ? 'ENABLED' : 'DISABLED';
    if (typeof message?.isGroupEnabledOrDisabled === 'boolean') {
        return message.isGroupEnabledOrDisabled ? 'ENABLED' : 'DISABLED';
    }
    return null;
};

const decodeMqttGroupState = (message: any): GroupState | null =>
    extractMqttStringState(message) ?? extractMqttBoolState(message);

const extractBackendMessage = (error: any): string => {
    const errMsg = error?.data?.errorMessage;
    const firstMsg = Array.isArray(errMsg) ? errMsg[0] : errMsg;
    return firstMsg ?? error?.data?.message ?? 'Failed to create load group';
};

const isValidationErrorStatus = (error: any): boolean => {
    const status = error?.status;
    return status === 400 || status === 409;
};

const clearPendingStates = (prev: Record<string, GroupState>, ids: string[]): Record<string, GroupState> => {
    const next = { ...prev };
    if (!ids.length) {
        Object.keys(next).forEach((id) => {
            if (next[id] === 'PENDING') {
                delete next[id];
            }
        });
        return next;
    }

    ids.forEach((id) => {
        const key = String(id);
        if (next[key] === 'PENDING') {
            delete next[key];
        }
    });
    return next;
};

const applyMqttStateToGroups = (
    prev: Record<string, GroupState>,
    ids: string[],
    mqttState: GroupState
): Record<string, GroupState> => {
    const next = { ...prev };
    if (!ids.length) {
        return clearPendingStates(next, []);
    }

    ids.forEach((id) => {
        next[String(id)] = mqttState;
    });

    return next;
};

const getToggleSuccessTranslationKey = (trigger: ChangeTrigger, isEnabled: boolean): string => {
    if (trigger === 'turnAllOn') return 'WIDGETS:LOADS_GROUPS.ALL_LOADS_TURNED_ON_SUCCESSFULLY';
    if (trigger === 'turnAllOff') return 'WIDGETS:LOADS_GROUPS.ALL_LOADS_TURNED_OFF_SUCCESSFULLY';
    return isEnabled
        ? 'WIDGETS:LOADS_GROUPS.LOAD_GROUP_TURN_ON_COMPLETED'
        : 'WIDGETS:LOADS_GROUPS.LOAD_GROUP_TURN_OFF_COMPLETED';
};

const renderToggleInitiatedMessage = (isEnabled: boolean, loadGroupName: any): JSX.Element => {
    if (isEnabled) {
        return (
            <CustomTransComponent
                translationKey={'WIDGETS:LOADS_GROUPS.LOAD_GROUP_TURN_ON_INITIATED'}
                replace={{ loadGroupName }}
            />
        );
    }
    return (
        <CustomTransComponent
            translationKey={'WIDGETS:LOADS_GROUPS.LOAD_GROUP_TURN_OFF_INITIATED'}
            replace={{ loadGroupName }}
        />
    );
};

const getCreateLoadGroupPrimaryAction = ({
    rejectedName,
    createPayload,
    selectedDeviceType,
    isCreateLoadGroupLoading,
    hasTextError,
    handleCreateLoadGroup,
    createDraftLoadGroup,
    openStepperForSingleDevice,
}: {
    rejectedName: string;
    createPayload: any;
    selectedDeviceType: string;
    isCreateLoadGroupLoading: boolean;
    hasTextError: boolean;
    handleCreateLoadGroup: () => Promise<any>;
    createDraftLoadGroup: () => void;
    openStepperForSingleDevice: () => void;
}): ModalActions => {
    if (rejectedName) {
        return {
            key: 'Save',
            disabled: computeCreateSaveDisabled({
                createPayload,
                rejectedName,
                isLoading: isCreateLoadGroupLoading,
                hasTextError,
            }),
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            handleClick: (): void => {
                void handleCreateLoadGroup();
            },
        };
    }

    return {
        key: 'Next',
        disabled: computeCreateNextDisabled({
            createPayload,
            selectedDeviceType,
            isLoading: isCreateLoadGroupLoading,
            hasTextError,
        }),
        label: <CustomTransComponent translationKey={'COMMON:NEXT'} />,
        variant: 'contained',
        handleClick: (): void => {
            if (createPayload?.isMultipleDevice) {
                createDraftLoadGroup();
                return;
            }

            openStepperForSingleDevice();
        },
    };
};

const buildInitialListingPayload = (deviceId: any, groupId: any, currentOrgId: any): any => ({
    page: 0,
    size: 10,
    filters: {},
    ...(deviceId && { deviceId: [deviceId] }),
    ...(groupId && groupId !== currentOrgId && { groupId: [groupId] }),
});

const buildInitialCreatePayload = (deviceId: any, groupId: any): any => ({
    description: '',
    modelCategory: '',
    isMultipleDevice: false,
    loadIds: [],
    name: '',
    ...(deviceId && { sourceDeviceId: deviceId }),
    ...(groupId && { sourceGroupId: groupId }),
});

type CreateSaveDisabledOptions = {
    createPayload: any;
    rejectedName: string;
    isLoading: boolean;
    hasTextError: boolean;
};

const computeCreateSaveDisabled = ({
    createPayload,
    rejectedName,
    isLoading,
    hasTextError,
}: CreateSaveDisabledOptions): boolean =>
    !createPayload?.name?.length || createPayload?.name === rejectedName || isLoading || hasTextError;

type CreateNextDisabledOptions = {
    createPayload: any;
    selectedDeviceType: string;
    isLoading: boolean;
    hasTextError: boolean;
};

const computeCreateNextDisabled = ({
    createPayload,
    selectedDeviceType,
    isLoading,
    hasTextError,
}: CreateNextDisabledOptions): boolean =>
    !createPayload?.name?.length ||
    (createPayload?.isMultipleDevice && !selectedDeviceType) ||
    isLoading ||
    hasTextError;

type SearchToolbarProps = {
    searchKey: any;
    onSearchChange: (value: string) => void;
    filters?: Record<string, any>;
    onChipDelete: (key: string) => void;
    canCreateLoadsGroups: boolean;
    onCreateLoad: () => void;
};

const SearchToolbar = ({
    searchKey,
    onSearchChange,
    filters,
    onChipDelete,
    canCreateLoadsGroups,
    onCreateLoad,
}: SearchToolbarProps): JSX.Element => {
    const { t } = useTranslation();
    const getLabelName = (key: string): string => {
        if (key === 'modelCategory') return t('COMMON:TYPE');
        if (key === 'groupState') return t('COMMON:STATE');
        return toFirstLetterUpperCase(key);
    };
    return (
        <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} spacing={10} p={3} justifyContent={'space-between'} alignItems={'center'}>
                <InputWithCustomPlaceholder
                    id="searchKey"
                    value={searchKey ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        onSearchChange(e.target.value);
                    }}
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                    size="small"
                    InputProps={{
                        startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                    }}
                />
            </Stack>
            <Stack direction="row" spacing={1}>
                {filters && (
                    <Stack direction="row" spacing={1}>
                        {Object.keys(filters).map((key: string) => (
                            <Chip
                                key={`unique${key}`}
                                label={getLabelName(key)}
                                onDelete={(): void => {
                                    onChipDelete(key);
                                }}
                            />
                        ))}
                        {canCreateLoadsGroups && (
                            <Box>
                                <Button id="fade-button" aria-haspopup="true" onClick={onCreateLoad}>
                                    <Tooltip
                                        title={
                                            <CustomTransComponent
                                                translationKey={'WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'}
                                            />
                                        }
                                        placement="top"
                                    >
                                        <AddBox sx={{ color: '#727E84' }} />
                                    </Tooltip>
                                </Button>
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

type LoadGroupsTableProps = {
    tableRef: React.RefObject<any>;
    columns: any[];
    payloadPage: number;
    onPageChange: (page: number, size: number) => void;
    loadGroupLists: any;
    onFilterChange: (filters: any, sortData?: any) => void;
    isFetching: boolean;
};

const LoadGroupsTable = ({
    tableRef,
    columns,
    payloadPage,
    onPageChange,
    loadGroupLists,
    onFilterChange,
    isFetching,
}: LoadGroupsTableProps): JSX.Element => {
    const { t } = useTranslation();
    const dataObj = loadGroupLists?.data ?? {};
    return (
        <Paper className="box-shadow">
            <CustomTable
                ref={tableRef}
                isPagination={true}
                keyToTraverse="id"
                headers={columns}
                handlePageChange={onPageChange}
                controlledPageSize={payloadPage}
                data={dataObj.records ?? []}
                handleFilterChange={onFilterChange}
                isLoading={isFetching}
                total={dataObj.total ?? 0}
                isWhiteRows={true}
                noDataFoundTitle={t('WIDGETS:LOADS_GROUPS.NO_LOAD_GROUPS')}
                noDataFoundIcon={<DynamicForm fontSize={'inherit'} sx={{ color: Colors.gray[500] }} />}
            />
        </Paper>
    );
};

type DeleteConfirmationModalProps = {
    isOpen: boolean;
    data: any;
    onCancel: () => void;
    onDelete: () => void;
};

const DeleteConfirmationModal = ({ isOpen, data, onCancel, onDelete }: DeleteConfirmationModalProps): JSX.Element => {
    const { t } = useTranslation();
    return (
        <CustomModal
            isOpen={isOpen}
            type="primary"
            header={t('WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_WITH_QUESTION_MARK')}
            actions={[
                {
                    key: 'cancel-action',
                    label: t('COMMON:CANCEL'),
                    variant: 'outlined',
                    handleClick: onCancel,
                },
                {
                    key: 'delete-action',
                    label: t('WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_BTN'),
                    variant: 'contained',
                    handleClick: onDelete,
                    styles: { backgroundColor: '#CA3C3D', color: '#fff' },
                },
            ]}
            hidePadding
            noDividers
            actionsDivider
        >
            <Stack component="form" spacing={2} noValidate autoComplete="off" m={2}>
                <Typography variant="body1">
                    <CustomTransComponent
                        translationKey={'WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_DESCRIPTION'}
                        replace={{
                            loadGroupName: data?.name,
                        }}
                    />
                </Typography>
            </Stack>
        </CustomModal>
    );
};

type CreateLoadGroupModalProps = {
    isOpen: boolean;
    onClose: () => void;
    actions: ModalActions[];
    createPayload: any;
    makeErrorHandler: (field: string) => (hasError: boolean) => void;
    handleChangeNameDescription: (e: any) => void;
    handleKeyDown: (e: any) => any;
    nameError: string;
    onChangeMultipleDevice: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectedDeviceType: string;
    onSelectMultiDeviceType: (event: SelectChangeEvent) => void;
    multiDeviceData: any;
};

const CreateLoadGroupModal = ({
    isOpen,
    onClose,
    actions,
    createPayload,
    makeErrorHandler,
    handleChangeNameDescription,
    handleKeyDown,
    nameError,
    onChangeMultipleDevice,
    selectedDeviceType,
    onSelectMultiDeviceType,
    multiDeviceData,
}: CreateLoadGroupModalProps): JSX.Element => {
    const { t } = useTranslation();
    const payloadObj = createPayload ?? {};
    const cpIsMultipleDevice = payloadObj.isMultipleDevice;

    return (
        <CustomModal
            isOpen={isOpen}
            isLoading={false}
            type="primary"
            onClose={onClose}
            actions={actions}
            customHeader={
                <>
                    <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'} />
                </>
            }
            actionsDivider={true}
        >
            <Box>
                <Typography variant="subtitle1" fontWeight={'600'} my={2} sx={{ wordWrap: 'break-word' }}>
                    <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.NEW_LOAD_GROUP_NAME_AND_DESCRIPTION'} />
                </Typography>
                <Box>
                    <LanguageTextField
                        id="name"
                        label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                        name="name"
                        multiline
                        variant="filled"
                        value={payloadObj.name}
                        onChange={handleChangeNameDescription}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        type="text"
                        inputProps={{ maxLength: 32 }}
                        {...(nameError && {
                            error: true,
                            helperText: nameError,
                            isErrorText: true,
                        })}
                        handleTextError={makeErrorHandler('name')}
                        className="margin-top-18"
                    />
                    <Stack direction={'row'} justifyContent={'space-between'} px={2}>
                        <FormHelperText>
                            <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                        </FormHelperText>
                        <FormHelperText>{payloadObj.name?.length}/32</FormHelperText>
                    </Stack>
                    <LanguageTextField
                        id="description"
                        label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                        name="description"
                        multiline
                        variant="filled"
                        value={payloadObj.description}
                        onChange={handleChangeNameDescription}
                        handleTextError={makeErrorHandler('description')}
                        fullWidth
                        inputProps={{ maxLength: 100 }}
                        className="margin-top-18"
                    />
                    <FormHelperText className="text-align-end padding-x-16">
                        {payloadObj.description?.length}/100
                    </FormHelperText>
                </Box>
                <Box>
                    <FormControl>
                        <FormLabel id="demo-controlled-radio-buttons-group" sx={{ py: 3 }}>
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.NEW_LOAD_GROUP_SUB_HEADING'} />
                        </FormLabel>
                        <Box px={3}>
                            <RadioGroup
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                name="controlled-radio-buttons-group"
                                value={String(cpIsMultipleDevice)}
                                onChange={onChangeMultipleDevice}
                            >
                                <FormControlLabel
                                    value={false}
                                    control={<Radio />}
                                    label={t('WIDGETS:LOADS_GROUPS.SPECIFIC_TO_A_SINGLE_DEVICE')}
                                />
                                <FormControlLabel
                                    value={true}
                                    control={<Radio />}
                                    label={t('WIDGETS:LOADS_GROUPS.CONTAINS_LOADS_ON_MULTIPLE_DEVICES')}
                                />
                            </RadioGroup>
                        </Box>
                    </FormControl>
                    {cpIsMultipleDevice && (
                        <Box mt={2} mb={3}>
                            <FormControl variant="filled" fullWidth>
                                <InputLabel id="demo-simple-select-filled-label">
                                    <CustomTransComponent
                                        translationKey={'WIDGETS:LOADS_GROUPS.MULTI_DEVICE_TYPE_SELECTION'}
                                    />
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-filled-label"
                                    value={selectedDeviceType}
                                    onChange={onSelectMultiDeviceType}
                                >
                                    {Array.isArray(multiDeviceData?.data) &&
                                        multiDeviceData.data.map((item: string) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </Box>
        </CustomModal>
    );
};

type LoadGroupsListingProps = {
    isRampShedInProgress?: boolean;
};

export const LoadGroupsListing: React.FC<LoadGroupsListingProps> = ({ isRampShedInProgress = false }) => {
    const { hasTextError, makeErrorHandler } = useTextFieldError();
    const handleKeyDown = (event: any): any => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canCreateLoadsGroups = hasPermission('load-groups_create-devices');
    const canEditLoadsGroups = hasPermission('load-groups_edit-devices');
    const canViewRedundancy = hasPermission('load-redundancy-management_view-devices');
    const canViewRedundancyExpired = hasPermission('load-redundancy-management_view-devices_hasExpired');
    const canDeleteLoadsGroups = hasPermission('load-groups_delete-devices');
    const tableRef = React.useRef<any>(null);
    const { onCancel, onClick, isVisible, data } = useConfirm();
    const [deleteLoadGroup] = useDeleteLoadGroupsMutation({ fixedCacheKey: 'deleteLoadGroup' });
    const [updateLoadGroups] = useUpdateLoadGroupsMutation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const handlePagination = (currentPage: number, pageSize: number): void => {
        setPayload((prev: any) => ({ ...prev, page: currentPage, size: pageSize }));
    };
    const [searchKey, setSearchKey] = React.useState<any>(undefined);
    const [nameError, setNameError] = React.useState('');
    // When the BE rejects a duplicate name we keep the original modal open and
    // switch the primary action to Save; track the rejected name so we can
    // disable the Save button until the user changes it.
    const [rejectedName, setRejectedName] = React.useState<string>('');
    const { deviceId, groupId } = useParams();
    const [isCreateLoadModalOpen, setIsCreateLoadModalOpen] = React.useState<boolean>(false);
    const location = useLocation();
    const hasOpenedCreateModalRef = React.useRef(false);
    const [, debouncedValue] = useDebounce(undefined, undefined, searchKey);
    const currentOrg = useTypedSelector(selectedOrg);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);
    const { data: profileData }: any = useGetUserProfileQuery({}, { refetchOnMountOrArgChange: true });
    useNewCustomTranslations('WIDGETS');
    const [payload, setPayload] = React.useState<any>(buildInitialListingPayload(deviceId, groupId, currentOrg?.id));
    const payloadObj = payload ?? {};
    const [loadGroupStates, setLoadGroupStates] = React.useState<Record<string, GroupState>>({});

    const markLoadGroupPending = React.useCallback((loadGroupId: number | string): void => {
        setLoadGroupStates((prev) => ({ ...prev, [String(loadGroupId)]: 'PENDING' }));
    }, []);

    const getLoadGroupIdsFromMqttMessage = React.useCallback((message: any): string[] => {
        const collectedIds = new Set<string>();
        const appendId = (value: any): void => {
            if (value !== undefined && value !== null && `${value}`.trim().length) {
                collectedIds.add(String(value));
            }
        };

        appendId(message?.loadGroupId);
        appendId(message?.loadGroupsItem?.id);
        appendId(message?.loadGroup?.id);

        if (Array.isArray(message?.loadGroupIds)) {
            message.loadGroupIds.forEach((id: any) => appendId(id));
        }

        return Array.from(collectedIds);
    }, []);

    const getLoadGroupStateFromMqttMessage = React.useCallback(
        (message: any): GroupState | null => decodeMqttGroupState(message),
        []
    );

    const handleFilterChange = (filters: any, sortData?: any): void => {
        setPayload((prev: any) => ({
            ...prev,
            page: 0,
            filters,
            ...(sortData && Object.keys(sortData)?.length ? { sort: sortData } : {}),
        }));
    };

    const resetCreatePayload = (): void => {
        setCreatePayload(buildInitialCreatePayload(deviceId, groupId));

        setSelectedDeviceId({});
        setSelectedDeviceType('');
        setSelectedMultiLoads([]);
        setNameError('');
        setRejectedName('');
    };

    const { data: multiDeviceData } = useGetMultiDeviceQuery(
        {
            params: { category: 'CATEGORY', assetCategory: 'LOAD_GROUPS' },
        },
        { skip: !currentOrg?.id }
    );
    const [selectedDeviceType, setSelectedDeviceType] = React.useState<string>('');
    const dispatch = useDispatch();
    const [createLoadGroup, { isLoading: isCreateLoadGroupLoading }] = useCreateLoadGroupsMutation();
    const [selectedDeviceId, setSelectedDeviceId] = React.useState<any>({});
    const [selectedMultiLoads, setSelectedMultiLoads] = React.useState<any[]>([]);
    const [createPayload, setCreatePayload] = React.useState<any>(buildInitialCreatePayload(deviceId, groupId));
    const [isStepperModalOpen, setIsStepperModalOpen] = React.useState(false);
    const getModifiedPayload = (): any => {
        const newPayload = JSON.parse(JSON.stringify(payload ?? {}));
        if (newPayload?.filters) {
            delete newPayload.filters;
        }
        return { ...newPayload, ...payload?.filters };
    };

    const {
        data: loadGroupLists,
        isFetching,
        refetch: refetchLoadGroups,
    }: any = useGetLoadGroupsListQuery(getModifiedPayload(), {
        refetchOnMountOrArgChange: true,
    });
    const { data: deviceData }: any = useGetDeviceByIdForLoadGroupsQuery(createPayload?.sourceDeviceId, {
        skip: !createPayload?.sourceDeviceId || !isStepperModalOpen,
        refetchOnMountOrArgChange: true,
    });
    const [currentStep, setCurrentStep] = React.useState<string>('selectDevice');
    const handleMultiDeviceChange = (event: SelectChangeEvent): void => {
        const value = event.target.value;

        setSelectedDeviceType(value);

        setCreatePayload((prev: any) => ({
            ...prev,
            modelCategory: value,
        }));
    };

    const getCurrentStep = (step: string): void => {
        setCurrentStep(step);
    };

    const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const isMultipleDevice = event?.target?.value === 'true';

        setCreatePayload((pre: any) => ({
            ...pre,
            isMultipleDevice,
            loadIds: [],
        }));
        setSelectedMultiLoads([]);
    };

    const modalContent = React.useMemo(
        () =>
            createPayload?.isMultipleDevice
                ? [
                      {
                          key: 'selectLoads',
                          title: t('WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'),
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_LOADS_TO_ADD_TO_THE_LOAD_GROUP'),
                          description: selectedDeviceType,
                          component: (
                              <MultiDeviceLoadStep
                                  createPayload={createPayload}
                                  setCreatePayload={setCreatePayload}
                                  selectedLoads={selectedMultiLoads}
                                  setSelectedLoads={setSelectedMultiLoads}
                              />
                          ),
                      },
                  ]
                : [
                      {
                          key: 'selectDevice',
                          title: t('WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'),
                          description: '',
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_DEVICE_TO_CREATE_A_LOAD_GROUP'),
                          component: (
                              <LoadHierarchy
                                  setSelectDeviceId={(device: any) => {
                                      setSelectedDeviceId(device);
                                      setCreatePayload((prev: any) => ({
                                          ...prev,
                                          sourceDeviceId: device?.id,
                                      }));
                                  }}
                                  selectedDeviceId={createPayload?.sourceDeviceId}
                              />
                          ),
                      },
                      {
                          key: 'selectLoads',
                          title: t('LOADS:ADD_LOADS'),
                          header: t('WIDGETS:LOADS_GROUPS.SELECT_LOADS_TO_ADD_TO_THE_LOAD_GROUP'),
                          description: (
                              <GroupPath deviceName={selectedDeviceId?.name} pathName={selectedDeviceId?.groupPath} />
                          ),
                          component: (
                              <LoadStepTwo
                                  selectedDevice={selectedDeviceId}
                                  createPayload={createPayload}
                                  setCreatePayload={setCreatePayload}
                              />
                          ),
                      },
                  ],
        [selectedDeviceId?.id, selectedDeviceType, selectedMultiLoads, createPayload]
    );

    const createDraftLoadGroup = (): void => {
        navigate(`/${currentRealmName}/load-groups`, {
            state: {
                from: location.pathname + location.hash,
                isDraft: true,
                createPayload: {
                    ...createPayload,
                    loadIds: selectedMultiLoads?.map?.((load: any) => load?.id || load?.loadId) ?? [],
                },
            },
        });

        setIsCreateLoadModalOpen(false);
    };

    const openStepperForSingleDevice = (): void => {
        setCurrentStep('selectDevice');
        setIsStepperModalOpen(true);
        setIsCreateLoadModalOpen(false);
    };

    const closeModal = (): void => {
        setIsStepperModalOpen(false);
        if (currentStep === 'selectDevice') {
            setIsCreateLoadModalOpen(true);
        }
    };

    const handleCheckDisabled = (): boolean => {
        const stepDisablers: Record<string, () => boolean> = {
            selectDevice: () => !createPayload?.sourceDeviceId?.length,
            selectLoads: () => !createPayload?.loadIds?.length,
        };
        return stepDisablers[currentStep]?.() ?? true;
    };

    const handleCreateLoadGroup = async (): Promise<any> => {
        const payloadToSend = {
            ...createPayload,
            modelCategory: createPayload.modelCategory,
            // Suppress the global error toast so duplicate-name errors render only
            // inline under the name field via setNameError below.
            suppressErrorToast: true,
        };

        const { error, data: loadGroup }: any = await createLoadGroup(payloadToSend);

        if (error) {
            const beMessage = extractBackendMessage(error);
            if (isValidationErrorStatus(error)) {
                setNameError(beMessage);
                // Remember the rejected name so the modal's primary action stays
                // disabled until the user enters a different one.
                setRejectedName(createPayload?.name ?? '');
                setIsStepperModalOpen(false);
                setIsCreateLoadModalOpen(true);
            } else {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: beMessage,
                        duration: 3000,
                    })
                );
            }
            return;
        }

        resetCreatePayload();
        setIsCreateLoadModalOpen(false);
        setIsStepperModalOpen(false);
        const newLoadGroupId = loadGroup?.data?.loadGroup?.id;
        navigate(`/${currentRealmName}/load-groups/${newLoadGroupId}`, {
            state: { from: location.pathname + location.hash },
        });
        dispatch(
            setMessageContent({
                isOpen: true,
                message: t('WIDGETS:LOADS_GROUPS.LOAD_GROUP_CREATED_SUCCESSFULLY'),
                duration: 3000,
            })
        );
    };

    const createLoadGroupPrimaryAction = getCreateLoadGroupPrimaryAction({
        rejectedName,
        createPayload,
        selectedDeviceType,
        isCreateLoadGroupLoading,
        hasTextError,
        handleCreateLoadGroup,
        createDraftLoadGroup,
        openStepperForSingleDevice,
    });

    const createLoadGroupModalActions: ModalActions[] = [
        {
            key: 'Cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                if (currentStep === 'stepOne') {
                    setIsStepperModalOpen(false);
                    closeModal();
                    setIsCreateLoadModalOpen(true);
                }
            },
        },
        createLoadGroupPrimaryAction,
    ];

    const handleDeleteLoadGroup = async (): Promise<void> => {
        const { error }: any = await deleteLoadGroup({
            id: [data?.id],
        });

        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:LOADS_GROUPS.SELECTED_LOAD_GROUP_DELETED', {
                        replace: { loadGroupName: data?.name },
                    }),
                })
            );
        }
        onCancel();
    };

    const handleRedundancyState = (item: any): JSX.Element => {
        if (!item?.isGroupRedundancyActive) return <></>;
        if (item?.isGroupRedundant === true) {
            return (
                <Tooltip title={t('WIDGETS:LOADS_GROUPS.REDUNDANCY_ACTIVE')} placement="top">
                    <JoinFull sx={{ color: '#39B620' }} />
                </Tooltip>
            );
        }
        if (item?.isGroupRedundant === false) {
            return (
                <Tooltip title={t('WIDGETS:LOADS_GROUPS.REDUNDANCY_ERROR')} placement="top">
                    <JoinInner sx={{ color: '#CA3C3D' }} />
                </Tooltip>
            );
        }
        return <></>;
    };

    const getRedundancyName = (item: any): JSX.Element => (
        <Typography sx={{ fontSize: 16, fontWeight: '400', color: '#424e54' }}>{item?.name}</Typography>
    );

    const getLoadCount = (item: any): JSX.Element => (
        <Typography sx={{ fontSize: 14, fontWeight: '400', color: '#424e54' }}>{`${item?.numberOfLoadsEnabled ?? 0}/${
            item?.totalLoads ?? 0
        }`}</Typography>
    );

    const getRedundancyPath = (item: any): JSX.Element => {
        if (item?.isMultipleDevice) {
            return (
                <Stack direction="row" alignItems="center" gap={1}>
                    <Link sx={{ color: '#727E84', fontSize: 16 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: '400', color: '#424e54' }}>
                        {t('WIDGETS:BALANCING.MULTIPLE_DEVICES')}
                    </Typography>
                </Stack>
            );
        }
        const itemObj = item ?? {};
        const webIcon = itemObj.deviceTypeIcon?.web;
        return (
            <Stack direction="row" alignItems="center">
                <Box pt={1} pr={1}>
                    <CustomIcon
                        iconProps={{ sx: { color: '#727E84', fontSize: 16 } }}
                        iconName={webIcon?.name}
                        family={webIcon?.family}
                    />
                </Box>
                <GroupPath
                    key={itemObj.id}
                    deviceName={itemObj.deviceName}
                    pathName={itemObj.groupPath}
                    width={'220px'}
                />
            </Stack>
        );
    };

    const getRedundancyDescription = (item: any): JSX.Element => (
        <Typography className="text-truncate max-width-256" sx={{ fontSize: 14, fontWeight: '400', color: '#424e54' }}>
            {item?.description}
        </Typography>
    );

    const renderToggleSnackbarAction = (snackbarKey: any): JSX.Element => (
        <Button variant="text" sx={{ color: '#80BDE0' }} onClick={() => closeSnackbar(snackbarKey)}>
            <CustomTransComponent translationKey={'bluiRegistration:ACTIONS.OKAY'} />
        </Button>
    );

    const getLoadGroupToggleSnackbarProps = (): any => ({
        variant: 'notificationWithIcon',
        autoHideDuration: 60000,
        Icon: <CircularProgress style={{ color: '#007bc1' }} size={24} />,
        notificationWithAction: true,
        notificationAction: renderToggleSnackbarAction,
    });

    const loadGroupFallbackTimeoutRefs = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const lastLoadGroupMqttAtRef = React.useRef<Record<string, number>>({});

    const handleLoadGroupToggle = async (
        loadGroup: any,
        newState: GroupState,
        trigger: ChangeTrigger = 'toggle'
    ): Promise<void> => {
        if (!loadGroup?.id) return;

        const isEnabled = newState === 'ENABLED';

        const snackbarKey = enqueueSnackbar(
            renderToggleInitiatedMessage(isEnabled, loadGroup?.name),
            getLoadGroupToggleSnackbarProps()
        );

        try {
            const { error }: any = await updateLoadGroups({
                loadGroupId: loadGroup.id,
                body: {
                    performGroupSwitchAction: true,
                    isGroupEnabledOrDisabled: isEnabled,
                },
            });

            if (error) {
                throw error;
            }

            markLoadGroupPending(loadGroup.id);
            const timeoutKey = String(loadGroup.id);
            if (loadGroupFallbackTimeoutRefs.current[timeoutKey]) {
                clearTimeout(loadGroupFallbackTimeoutRefs.current[timeoutKey]!);
            }
            const toggleStartedAt = Date.now();
            loadGroupFallbackTimeoutRefs.current[timeoutKey] = setTimeout(() => {
                const lastMqttAt = lastLoadGroupMqttAtRef.current[timeoutKey] ?? 0;
                if (lastMqttAt >= toggleStartedAt) return;

                void refetchLoadGroups().finally(() => {
                    setLoadGroupStates((prev) => {
                        if (prev[timeoutKey] !== 'PENDING') return prev;
                        const next = { ...prev };
                        delete next[timeoutKey];
                        return next;
                    });
                });
            }, 60_000);

            closeSnackbar(snackbarKey);
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t(getToggleSuccessTranslationKey(trigger, isEnabled), {
                        replace: { loadGroupName: loadGroup?.name },
                    }),
                    duration: 3000,
                })
            );
        } catch (error) {
            closeSnackbar(snackbarKey);
            throw error;
        }
    };

    const getRedundancyState = (item: any): JSX.Element => {
        const itemObj = item ?? {};
        const isViewer = !(canCreateLoadsGroups || canEditLoadsGroups);
        const overriddenState = loadGroupStates[String(itemObj.id)];
        const isPending = overriddenState === 'PENDING';
        const isQuarantineOrOffline = Boolean(itemObj.quarantineOrOffline);
        const isDisabled = isViewer || isPending || isRampShedInProgress || isQuarantineOrOffline;
        return (
            <CustomSwitchBtn
                id={itemObj.id}
                totalLoadCount={itemObj.totalLoads}
                enabledLoadCount={itemObj.numberOfLoadsEnabled}
                loadGroupName={itemObj.name}
                isEnabled={itemObj.isGroupEnabledOrDisabled}
                disabled={isDisabled}
                onChange={(_id, newState, trigger): void => {
                    void handleLoadGroupToggle(item, newState, trigger);
                }}
                groupState={overriddenState ?? itemObj.groupState}
            />
        );
    };

    const getRedundancyDeleteBtn = (item: any): any => (
        <IconButton
            onClick={() => {
                if (isRampShedInProgress) return;
                onClick(item);
            }}
            disabled={isRampShedInProgress}
            sx={{
                cursor: isRampShedInProgress ? 'not-allowed' : 'pointer',
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                },
            }}
        >
            <Delete sx={{ color: '#727E84' }} />
        </IconButton>
    );

    const getChevronIcon = (item: any): any => (
        <IconButton
            onClick={() => {
                if (isRampShedInProgress) return;
                navigate(`/${currentRealmName}/load-groups/${item?.id}`, {
                    state: { from: location.pathname + location.hash },
                });
            }}
            disabled={isRampShedInProgress}
            sx={{
                cursor: isRampShedInProgress ? 'not-allowed' : 'pointer',
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                },
            }}
        >
            <ChevronRight sx={{ color: '#424e54' }} />
        </IconButton>
    );
    const columns: any = React.useMemo(
        () => [
            {
                header: t('COMMON:NAME'),
                width: '25%',
                accessor: 'name',
                isFilterable: true,
                isSortable: true,
                cell: getRedundancyName,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            ...(canViewRedundancy || canViewRedundancyExpired
                ? [
                      {
                          header: t('WIDGETS:LOADS_GROUPS.REDUNDANCY'),
                          width: '5%',
                          accessor: 'redundancy',
                          cell: (dataObj: any): JSX.Element => (
                              <Box display="flex" justifyContent="center" width="100%">
                                  {handleRedundancyState(dataObj)}
                              </Box>
                          ),
                          align: 'center',
                          isFilterable: true,
                          isSortable: true,
                          filterOptions: [
                              {
                                  id: 'all',
                                  label: (
                                      <Typography fontSize={16} fontWeight={400} color="#424E54">
                                          All
                                      </Typography>
                                  ),
                              },
                              {
                                  id: 'IN_REDUNDANCY',
                                  label: (
                                      <Typography fontSize={16} fontWeight={400} color="#424E54">
                                          In Redundancy
                                      </Typography>
                                  ),
                              },
                              {
                                  id: 'NO_REDUNDANCY',
                                  label: (
                                      <Typography fontSize={16} fontWeight={400} color="#424E54">
                                          No Redundancy
                                      </Typography>
                                  ),
                              },
                              {
                                  id: 'OUT_REDUNDANCY',
                                  label: (
                                      <Typography fontSize={16} fontWeight={400} color="#424E54">
                                          Out of Redundancy
                                      </Typography>
                                  ),
                              },
                          ],
                          skeleton: (
                              <>
                                  <Skeleton animation="wave" variant="text"></Skeleton>
                              </>
                          ),
                      },
                  ]
                : []),
            {
                header: t('WIDGETS:LOADS_GROUPS.LOAD_COUNT'),
                width: '8%',
                accessor: 'totalLoads',
                cell: (item: any): JSX.Element => (
                    <Box display="flex" justifyContent="center" width="100%">
                        {getLoadCount(item)}
                    </Box>
                ),
                align: 'center',
                isSortable: true,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: t('COMMON:TYPE'),
                width: '30%',
                accessor: 'modelCategory',
                cell: getRedundancyPath,
                isFilterable: true,
                isSortable: true,
                filterOptions: [
                    {
                        id: 'all',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                All
                            </Typography>
                        ),
                    },
                    {
                        id: 'ATS-PDU',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                ATS-PDU
                            </Typography>
                        ),
                    },
                    {
                        id: 'UPS',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                UPS
                            </Typography>
                        ),
                    },
                ],
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: t('COMMON:DESCRIPTION'),
                width: '22%',
                accessor: 'description',
                isFilterable: true,
                isSortable: true,
                cell: getRedundancyDescription,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            {
                header: t('COMMON:STATE'),
                width: '5%',
                accessor: 'groupState',
                isFilterable: true,
                isSortable: true,
                filterOptions: [
                    {
                        id: 'all',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                All
                            </Typography>
                        ),
                    },
                    {
                        id: 'PARTIAL',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                Indeterminate
                            </Typography>
                        ),
                    },
                    {
                        id: 'DISABLED',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                Off
                            </Typography>
                        ),
                    },
                    {
                        id: 'ENABLED',
                        label: (
                            <Typography fontSize={16} fontWeight={400} color="#424E54">
                                On
                            </Typography>
                        ),
                    },
                ],
                cell: getRedundancyState,
                skeleton: (
                    <>
                        <Skeleton animation="wave" variant="text"></Skeleton>
                    </>
                ),
            },
            ...(canDeleteLoadsGroups
                ? [
                      {
                          header: '',
                          width: '5%',
                          accessor: 'delete',
                          cell: getRedundancyDeleteBtn,
                          skeleton: <Skeleton animation="wave" variant="text" />,
                      },
                  ]
                : []),
            ...(canEditLoadsGroups
                ? [
                      {
                          header: '',
                          width: '5%',
                          accessor: 'chevron',
                          isSortable: false,
                          cell: getChevronIcon,
                          skeleton: <Skeleton animation="wave" variant="text" />,
                      },
                  ]
                : []),
        ],
        [
            t,
            canViewRedundancy,
            canViewRedundancyExpired,
            canDeleteLoadsGroups,
            canEditLoadsGroups,
            getRedundancyName,
            getLoadCount,
            getRedundancyPath,
            getRedundancyDescription,
            getRedundancyState,
            getRedundancyDeleteBtn,
            getChevronIcon,
            handleRedundancyState,
        ]
    );
    const handleCreateLoad = (): any => {
        resetCreatePayload();
        setIsCreateLoadModalOpen(true);
    };
    const handleChangeNameDescription = (e: any): void => {
        const { name, value } = e.target;

        if (name === 'name') {
            if (value.startsWith(' ')) {
                setNameError('Please provide a valid name.');
            } else {
                setNameError('');
            }
        }

        setCreatePayload((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChipDelete = (key: any): void => {
        tableRef?.current?.resetFilters(key === 'groupId' ? 'groupPath' : key, true);
        const paginationPayloadClone = JSON.parse(JSON.stringify(payload));
        if (key === 'groupIds' && paginationPayloadClone?.filters['deviceIds']) {
            delete paginationPayloadClone?.filters['deviceIds'];
        }
        delete paginationPayloadClone?.filters[key];
        setPayload(paginationPayloadClone);
    };

    React.useEffect(() => {
        if (deviceData?.data) {
            if (deviceData.data.modelCategory) {
                setCreatePayload((prev: any) => ({
                    ...prev,
                    modelCategory: deviceData.data.modelCategory,
                }));
            }
            if (!selectedDeviceId?.id) {
                setSelectedDeviceId({
                    id: deviceData.data.id,
                    name: deviceData.data.name,
                    groupPath: deviceData.data.groupPath,
                });
            }
        }
    }, [deviceData]);

    React.useEffect(() => {
        if (!hasOpenedCreateModalRef.current && location.state?.openCreateLoadGroup) {
            setIsCreateLoadModalOpen(true);
            hasOpenedCreateModalRef.current = true;
        }
    }, [location.state]);

    const localLoadGroupTopics = React.useMemo(() => {
        const records = loadGroupLists?.data?.records ?? [];
        return buildLoadGroupMqttTopicsForRecords(records, {
            realmId: profileData?.data?.currentRealmId ?? '',
            orgId: currentOrg?.id,
        });
    }, [loadGroupLists, profileData, currentOrg]);

    React.useEffect(() => {
        if (debouncedValue !== undefined)
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                globalSearch: debouncedValue,
            }));
    }, [debouncedValue]);

    const mqttLoadGroupHandler = (message: any, messageTopic: string): void => {
        if (message?.type === 'FAILED_LOAD_TRIGGER_FOR_LOAD_GROUP' && localLoadGroupTopics.includes(messageTopic)) {
            dispatch(setFailedLoadTrigger(message));
        }
        if (message?.type !== 'LOAD_GROUP_STATE_CHANGE') return;

        const idsFromMessage = getLoadGroupIdsFromMqttMessage(message);
        const mqttState = getLoadGroupStateFromMqttMessage(message);
        idsFromMessage.forEach((id) => {
            lastLoadGroupMqttAtRef.current[id] = Date.now();
        });

        if (!mqttState) {
            void refetchLoadGroups().finally(() => {
                setLoadGroupStates((prev) => clearPendingStates(prev, idsFromMessage));
            });
            return;
        }

        setLoadGroupStates((prev) => applyMqttStateToGroups(prev, idsFromMessage, mqttState));
    };

    React.useEffect(() => {
        if (mqttConnectionStatus === true && localLoadGroupTopics.length) {
            subscribeTopic(localLoadGroupTopics, mqttLoadGroupHandler);
        }

        return (): void => {
            if (localLoadGroupTopics.length) {
                unsubscribeTopic(localLoadGroupTopics);
            }
        };
    }, [mqttConnectionStatus, localLoadGroupTopics, mqttLoadGroupHandler]);

    React.useEffect(
        () => () => {
            Object.values(loadGroupFallbackTimeoutRefs.current).forEach((timeoutId) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
        },
        []
    );

    return (
        <Box>
            <SearchToolbar
                searchKey={searchKey}
                onSearchChange={setSearchKey}
                filters={payloadObj.filters}
                onChipDelete={handleChipDelete}
                canCreateLoadsGroups={canCreateLoadsGroups}
                onCreateLoad={handleCreateLoad}
            />
            <LoadGroupsTable
                tableRef={tableRef}
                columns={columns}
                payloadPage={payloadObj.page}
                onPageChange={handlePagination}
                loadGroupLists={loadGroupLists}
                onFilterChange={handleFilterChange}
                isFetching={isFetching}
            />
            <DeleteConfirmationModal
                isOpen={isVisible}
                data={data}
                onCancel={onCancel}
                onDelete={(): void => {
                    void handleDeleteLoadGroup();
                }}
            />

            <CreateLoadGroupModal
                isOpen={isCreateLoadModalOpen}
                onClose={(): void => {
                    resetCreatePayload();
                    setIsCreateLoadModalOpen(false);
                }}
                actions={createLoadGroupModalActions}
                createPayload={createPayload}
                makeErrorHandler={makeErrorHandler}
                handleChangeNameDescription={handleChangeNameDescription}
                handleKeyDown={handleKeyDown}
                nameError={nameError}
                onChangeMultipleDevice={handleChange}
                selectedDeviceType={selectedDeviceType}
                onSelectMultiDeviceType={handleMultiDeviceChange}
                multiDeviceData={multiDeviceData}
            />
            <CustomModal
                isOpen={isDeleteModalVisible}
                type="primary"
                header={t('WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_WITH_QUESTION_MARK')}
                actions={[
                    {
                        key: 'cancel-action',
                        label: t('COMMON:CANCEL'),
                        variant: 'outlined',
                        handleClick: (): void => setIsDeleteModalVisible(false),
                    },
                    {
                        key: 'delete-action',
                        label: t('WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_BTN'),
                        variant: 'contained',
                        handleClick: (): void => setIsDeleteModalVisible(false),
                        styles: { backgroundColor: '#CA3C3D', color: '#fff' },
                    },
                ]}
                hidePadding
                noDividers
                actionsDivider
            >
                <Stack component="form" spacing={2} noValidate autoComplete="off" m={2}>
                    <Typography variant="body1">
                        <CustomTransComponent
                            translationKey={'WIDGETS:LOADS_GROUPS.DELETE_LOAD_GROUP_DESCRIPTION'}
                            replace={{
                                loadGroupName: 'load name',
                            }}
                        />
                    </Typography>
                </Stack>
            </CustomModal>
            <FailedLoadTriggerModal
                onClose={(): void => {
                    refetchLoadGroups();
                    setLoadGroupStates({});
                }}
            />
            <StepperModal
                maxWidth={'lg'}
                modalContent={modalContent}
                isOpen={isStepperModalOpen}
                closeModal={closeModal}
                getCurrentStep={getCurrentStep}
                currentStepPassed={currentStep}
                setCurrentStep={setCurrentStep}
                isDisabled={handleCheckDisabled()}
                gotoBackStep={(): any => setCurrentStep('selectDevice')}
                submitHandler={(): any => handleCreateLoadGroup()}
                sx={{ height: 'calc(100vh - 150px)', width: '900px' }}
                isNextButton={false}
                isBackButton={true}
            />
        </Box>
    );
};
